
import { useQuery } from '@tanstack/react-query';

export interface VideoDisposition {
    default: number;
    dub: number;
    original: number;
    comment: number;
    lyrics: number;
    karaoke: number;
    forced: number;
    hearing_impaired: number;
    visual_impaired: number;
    clean_effects: number;
    attached_pic: number;
    timed_thumbnails: number;
    captions: number;
    descriptions: number;
    metadata: number;
    dependent: number;
    still_image: number;
}

export interface VideoTags {
    DURATION: string;
}

export interface VideoInfo {
    index: number;
    codec_name: string;
    codec_long_name: string;
    profile: string;
    codec_type: string;
    codec_tag_string: string;
    codec_tag: string;
    width: number;
    height: number;
    coded_width: number;
    coded_height: number;
    closed_captions: number;
    film_grain: number;
    has_b_frames: number;
    sample_aspect_ratio: string;
    display_aspect_ratio: string;
    pix_fmt: string;
    level: number;
    color_range: string;
    color_space: string;
    color_transfer: string;
    color_primaries: string;
    chroma_location: string;
    field_order: string;
    refs: number;
    is_avc: string;
    nal_length_size: string;
    r_frame_rate: string;
    avg_frame_rate: string;
    time_base: string;
    start_pts: number;
    start_time: string;
    bits_per_raw_sample: string;
    extradata_size: number;
    disposition: VideoDisposition;
    tags: VideoTags;
}

export interface AudioTags {
    language: string;
    title: string;
    DURATION: string;
}

export interface AudioInfo {
    index: number;
    codec_name: string;
    codec_long_name: string;
    profile: string;
    codec_type: string;
    codec_tag_string: string;
    codec_tag: string;
    sample_fmt: string;
    sample_rate: string;
    channels: number;
    channel_layout: string;
    bits_per_sample: number;
    r_frame_rate: string;
    avg_frame_rate: string;
    time_base: string;
    start_pts: number;
    start_time: string;
    extradata_size: number;
    disposition: VideoDisposition;
    tags: AudioTags;
}

export interface EpisodeInfo {
    air_date: string;
    rating: number;
    id: number;
    movie_image: string;
    duration_secs: number;
    duration: string;
    video: VideoInfo;
    audio: AudioInfo;
    bitrate: number;
}

export interface Episode {
    id: string;
    episode_num: number;
    title: string;
    container_extension: string;
    info: EpisodeInfo;
    custom_sid: string | null;
    added: string;
    season: number;
    direct_source: string;
}

export interface SeasonMetadata {
    name: string;
    episode_count: string;
    overview: string;
    air_date: string;
    cover: string;
    cover_tmdb: string;
    season_number: number;
    cover_big: string;
    releaseDate: string;
    duration: string;
}

export interface SeriesInfoData {
    name: string;
    cover: string;
    plot: string;
    cast: string;
    director: string;
    genre: string;
    releaseDate: string;
    release_date: string;
    last_modified: string;
    rating: string;
    rating_5based: string;
    backdrop_path: string[];
    tmdb: string;
    youtube_trailer: string;
    episode_run_time: string;
    category_id: string;
    category_ids: number[];
}

export interface SeriesInfo {
    seasons: SeasonMetadata[];
    info: SeriesInfoData;
    episodes: {
        [seasonNumber: string]: Episode[];
    };
}

export interface UseSeriesInfoParams {
    baseUrl: string;
    username: string;
    password: string;
    seriesId: string;
}

export const useSeriesInfo = ({ baseUrl, username, password, seriesId }: UseSeriesInfoParams) => {
    return useQuery<SeriesInfo>({
        queryKey: ['seriesInfo', baseUrl, username, seriesId],
        queryFn: async () => {
            const url = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=get_series_info&series_id=${seriesId}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch series info');
            }

            return response.json();
        },
        enabled: !!baseUrl && !!username && !!password && !!seriesId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};